<template>
  <div class="delivery-timing-section">
    <!-- Execution Method -->
    <div class="mb-4">
      <label class="form-label">
        {{ $t("production_strategy.execution_method") }}
      </label>
      <SegmentedControl
        :modelValue="actionInstructions.delivery_method"
        :options="deliveryMethodOptions"
        :aria-label="$t('production_strategy.execution_method_aria')"
        @update:modelValue="updateField('delivery_method', $event)"
      />

      <InfoCallout
        :icon="
          actionInstructions.delivery_method === DeliveryMethod.AUTOMATIC
            ? 'robot'
            : 'hand-paper'
        "
        class="mt-2"
      >
        <span
          v-if="actionInstructions.delivery_method === DeliveryMethod.AUTOMATIC"
        >
          <strong>{{ $t("production_strategy.automatic") }}</strong>
          — {{ $t("production_strategy.automatic_explainer") }}
        </span>
        <span v-else>
          <strong>{{ $t("production_strategy.manual") }}</strong>
          — {{ $t("production_strategy.manual_explainer") }}
        </span>
      </InfoCallout>
    </div>

    <!-- Delivery Timing -->
    <div class="mb-4">
      <label class="form-label">
        <FontAwesomeIcon icon="clock" class="me-1" />
        {{ $t("production_strategy.execution_timing") }}
      </label>
      <select
        class="form-select"
        :value="actionInstructions.delivery_timing"
        @change="handleDeliveryTimingChange"
        @blur="v$.delivery_timing.$touch()"
        :class="{ 'is-invalid': hasDeliveryTimingError }"
      >
        <option value="">{{ $t("common.select_option") }}</option>
        <option value="YEARLY">{{ $t("production_strategy.yearly") }}</option>
        <option value="HALF_YEAR">
          {{ $t("production_strategy.half_year") }}
        </option>
        <option value="QUARTERLY">
          {{ $t("production_strategy.quarterly") }}
        </option>
        <option value="MONTHLY">
          {{ $t("production_strategy.monthly") }}
        </option>
        <option value="FIXED_DATES">
          {{ $t("production_strategy.fixed_dates") }}
        </option>
      </select>
      <div v-if="hasDeliveryTimingError" class="invalid-feedback">
        <div v-for="error in deliveryTimingErrors" :key="error">
          {{ error }}
        </div>
      </div>
    </div>

    <!-- Delivery Dates Configuration -->
    <div v-if="actionInstructions.delivery_timing" class="mt-4">
      <RecurringDatesEditor
        :model-value="actionInstructions.recurring_date_specs"
        :execution-timing="actionInstructions.delivery_timing"
        @update:model-value="updateRecurringDates"
        @field-touched="v$.recurring_date_specs.$touch()"
        :has-validation-error="hasRecurringDatesError"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoCallout from "@/components/common/InfoCallout.vue";
import SegmentedControl, {
  type SegmentedControlOption,
} from "@/components/common/SegmentedControl.vue";
import { useValidators } from "@/composables/useValidators";
import {
  getExpectedDateCount,
  initializeRecurringDateSpecs,
} from "@/dashboard/production_strategy/overview/PPA/ppaActionUtils";
import type {
  ActionInstructions,
  DeliveryTiming,
  RecurringDateSpec,
} from "@/types";
import { DeliveryMethod } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import RecurringDatesEditor from "./RecurringDatesEditor.vue";

const actionInstructions = defineModel<ActionInstructions>({ required: true });

defineEmits<{
  "field-touched": [field: string];
}>();

const { t } = useI18n();
const customValidators = useValidators(t);

const deliveryMethodOptions = computed(
  (): SegmentedControlOption<DeliveryMethod>[] => [
    {
      value: DeliveryMethod.AUTOMATIC,
      label: t("production_strategy.automatic"),
      icon: "robot",
    },
    {
      value: DeliveryMethod.MANUAL,
      label: t("production_strategy.manual"),
      icon: "hand-paper",
    },
  ]
);

// Validation rules for this component
const rules = computed(() => ({
  delivery_timing: {
    required: customValidators.required,
  },
  // Timing-specific requirements
  recurring_date_specs: {
    requiredForAllTiming: helpers.withMessage(
      t("validations.required"),
      (dates: RecurringDateSpec[]) => {
        const instr = actionInstructions.value;
        if (!instr || !instr.delivery_timing) return true;

        // Validate correct number of dates based on timing
        const expectedCount = getExpectedDateCount(instr.delivery_timing);
        if (expectedCount !== -1 && dates.length !== expectedCount) {
          return false;
        }

        // For FIXED_DATES, at least one date required
        if (expectedCount === -1 && dates.length === 0) {
          return false;
        }

        // All dates must be valid
        return dates.every(
          (d) =>
            typeof d?.day === "number" &&
            d.day > 0 &&
            typeof d?.month === "number" &&
            d.month > 0
        );
      }
    ),
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    delivery_timing: actionInstructions.value.delivery_timing,
    recurring_date_specs: actionInstructions.value.recurring_date_specs,
  })),
  {
    $autoDirty: true,
  }
);

const hasDeliveryTimingError = computed(() => v$.value.delivery_timing.$error);
const hasRecurringDatesError = computed(
  () => v$.value.recurring_date_specs.$error
);

const deliveryTimingErrors = computed(
  () => v$.value.delivery_timing.$errors?.map((e) => e.$message as string) || []
);

const updateField = (field: keyof ActionInstructions, value: any) => {
  actionInstructions.value = { ...actionInstructions.value, [field]: value };
};

const handleDeliveryTimingChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value as DeliveryTiming;
  actionInstructions.value = initializeRecurringDateSpecs(
    actionInstructions.value,
    value
  );
};

const updateRecurringDates = (dates: RecurringDateSpec[]) => {
  actionInstructions.value = {
    ...actionInstructions.value,
    recurring_date_specs: dates,
  };
};

// Expose validation methods for parent component
defineExpose({
  isValid: () => !v$.value.$invalid,
  touchAndValidate: () => {
    v$.value.$touch();
    return !v$.value.$invalid;
  },
});
</script>
